<script setup lang="ts">
import { computed, onMounted, ref, useTemplateRef, watch } from 'vue';
import { useShapeLayer } from 'shared/map/layers/shapeLayer';
import { useVehiclesLayer } from 'shared/map/layers/vehicleLayer';
import MainLayout from '@/common/components/MainLayout.vue';
import ShapeDrawer from './components/ShapeDrawer.vue';
import { Assert } from 'shared/assert';
import { ContextMenu, Dialog, Select } from 'primevue';
import type { MenuItem } from 'primevue/menuitem';
import type { Coordinate } from 'shared/map/domain/coordinate';
import { useSelectedShapeStore } from './stores/selectedShapeStore';
import LogYardEditContentDialog from '@/storageAreas/logYards/components/LogYardEditContentDialog.vue';
import LogSorterDrawer from './components/LogSorterDrawer.vue';
import { useSelectedLogSorterStore, type EditLogSorter } from './stores/selectedLogSorterStore';
import { isBulkStorage, isCoilStorage, isLogYard, StorageAreaType } from 'shared/storageAreas/domain/storageAreaType';
import MapStyleSelect from '@/mapStyles/components/MapStyleSelect.vue';
import { useActionFailedAlert } from 'shared/problems/composables/actionFailedAlert';
import { storeToRefs } from 'pinia';
import { useLanguageStore } from 'shared/translations/stores/languageStore';
import UnsavedChangesDialog from 'shared/common/components/UnsavedChangesDialog.vue';
import { useShapeAuditApi } from '@/audit/api/shapeAuditApi';
import AuditDialog from 'shared/audit/components/AuditDialog.vue';
import { useVehicleAuditApi } from '@/audit/api/vehicleAuditApi';
import { useStorageAreaStore } from 'shared/storageAreas/stores/storageAreaStore';
import { useNewPosition } from './composables/newPosition';
import MoveCoilDialog from '@/coils/components/MoveCoilDialog.vue';
import { useSelectedStorageAreaStore } from './stores/selectedStorageAreaStore';
import { useSettingStore } from 'shared/setting/stores/settingStore';
import MoveLogYardContentDialog from '@/storageAreas/logYards/components/MoveLogYardContentDialog.vue';
import Button from 'primevue/button';
import { useAuthStore } from '@/auth/store/authStore';
import { useRefereshProductStore } from '@/products/commands/refreshProductStore';
import SearchLogYardDialog from 'shared/search/components/SearchLogYardDialog.vue';
import { useSearchProduct } from 'shared/search/composables/searchProduct';
import { useRefereshProductGroupStore } from '@/products/groups/commands/refreshProductGroupStore';
import { useRefreshSettingStore } from '@/settings/stores/refreshSettingStore';
import { useRefreshShapeStore } from '@/shapes/stores/refreshShapeStore';
import { useRefreshStorageAreaStore } from '@/storageAreas/stores/refreshStorageAreaStore';
import { useRefreshVehicleStore } from '@/vehicles/stores/refreshVehicleStore';
import { useRefreshCoilStore } from '@/coils/stores/refreshCoilStore';
import MapView, { type MapApi, type MapSettingOptions } from './components/MapView.vue';
import type { LogSorterResponse } from '@/logSorter/api/common/logSorterResponse';
import { useGetLogSorters } from '@/logSorter/api/getLogSorters';
import { useMoveTimberFromLogSorterBinsApi } from '@/moves/api/moveTimberFromLogSorterBinsApi';
import { useFeatureFlagStore } from '@/featureFlags/stores/featureFlagStore';
import { useRefreshProductionOrderStore } from '@/productionOrders/stores/refreshProductionOrderStore';
import OLMap from 'ol/Map';
import View from 'ol/View';
import TileLayer from 'ol/layer/Tile';
import XYZ from 'ol/source/XYZ';
import { useGeographic } from 'ol/proj';
import type Feature from 'ol/Feature';
import type { Geometry } from 'ol/geom';
import { getShapeId, getGeoJsonPropertyText, getStorageAreaId } from 'shared/shapes/domain/shapeFeature';
import { usePreviewRectangleLayer } from 'shared/map/layers/previewRectangleLayer';
import { usePreviewPolygonLayer } from 'shared/map/layers/previewPolygonLayer';
import { usePreviewLogsorterLayer } from './layers/previewLogSorterLayer';
import BulkEditContentDialog from '@/storageAreas/bulk/components/BulkEditContentDialog.vue';
import MoveBulkContentDialog from '@/storageAreas/bulk/components/MoveBulkContentDialog.vue';

useGeographic();

const shapeLayer = useShapeLayer();
const vehiclesLayer = useVehiclesLayer();
const selectedShapeStore = useSelectedShapeStore();
const selectedLogSorterStore = useSelectedLogSorterStore();
const previewLogsorterLayer = usePreviewLogsorterLayer();
const refreshProductStore = useRefereshProductStore();
const actionFailedAlert = useActionFailedAlert();
const shapeAuditApi = useShapeAuditApi();
const vehicleAuditApi = useVehicleAuditApi();
const storageAreaStore = useStorageAreaStore();
const authStore = useAuthStore();
const selectedStorageAreaStore = useSelectedStorageAreaStore();
const { currentStorageArea: selectedStorageArea } = storeToRefs(selectedStorageAreaStore);
const settingStore = useSettingStore();
const refreshProductGroupStore = useRefereshProductGroupStore();
const refreshSettingStore = useRefreshSettingStore();
const refreshShapeStore = useRefreshShapeStore();
const refreshStorageAreaStore = useRefreshStorageAreaStore();
const refreshVehicleStore = useRefreshVehicleStore();
const refreshCoilStore = useRefreshCoilStore();
const refreshProductionOrderStore = useRefreshProductionOrderStore();
const getLogSorters = useGetLogSorters();
const moveTimberFromLogSorterBinsApi = useMoveTimberFromLogSorterBinsApi();
const featureFlags = useFeatureFlagStore();

const { activeTab, selectionEnabled, selectedShapeIds } = storeToRefs(selectedShapeStore);
const { type: selectedStorageAreaType } = storeToRefs(selectedStorageAreaStore);

const previewRectangleLayer = usePreviewRectangleLayer();
const previewPolygonLayer = usePreviewPolygonLayer({
  selectedShapeIds,
  selectionEnabled
});

function updatePreviewLayers() {
  const previewActive = activeTab.value === 'shape' &&
    selectedStorageAreaType.value !== StorageAreaType.LogSorterBin;
  previewRectangleLayer.setActive(previewActive);
  previewPolygonLayer.setActive(previewActive);
}

watch([activeTab, selectedStorageAreaType], updatePreviewLayers, { immediate: true });

const newPosition = useNewPosition();

const rootElementRef = useTemplateRef('map-view');
const unsavedChangesDialog = useTemplateRef('unsaved-changes');
const shapeDrawer = useTemplateRef('shape-drawer');
const auditDialog = useTemplateRef('audit-dialog');
const moveCoilDialog = useTemplateRef('move-coil-dialog');
const moveLogYardContentDialog = useTemplateRef('move-log-yard-content-dialog');
const moveBulkContentDialog = useTemplateRef('move-bulk-content-dialog');
const searchLogYardDialogRef = useTemplateRef('search-log-yard-dialog');

const contextMenuDisabled = computed(() => {
  return selectedLogSorterStore.operation !== 'none';
});

const saveMapModeLoading = ref<boolean>(false);

const mapApi = ref<MapApi>();

const languageStore = useLanguageStore();
const { translations } = storeToRefs(languageStore);

let contextMenuCoordinates: Coordinate | null = null;
let contextMenuFeature: Feature<Geometry> | null = null;

const logSorters = ref<LogSorterResponse[]>([]);
const selectLogSorterDialogVisible = ref(false);
const selectedLogSorter = ref<LogSorterResponse>();

const menuItems = computed<MenuItem[]>(() => {

  const contextMenuTranslations = translations.value.map.contextmenu;

  return [
    {
      label: contextMenuTranslations.create.title,
      visible: () => {
        return contextMenuFeature === null && authStore.isAdmin;
      },
      items: [
        {
          label: contextMenuTranslations.create.shape,
          command() {
            Assert.definied(contextMenuCoordinates);
            selectedShapeStore.create({
              coordinate: contextMenuCoordinates,
            });
          },
          visible() {
            return authStore.isAdmin;
          }
        },
        {
          label: contextMenuTranslations.create.storageArea,
          command() {
            Assert.definied(contextMenuCoordinates);

            const settings = settingStore.setting;

            selectedShapeStore.create({
              coordinate: contextMenuCoordinates,
              width: settings?.sharedClient.storageArea.logYardWidth ?? 8,
              length: settings?.sharedClient.storageArea.logYardLength ?? 20,
              sideArea: settings?.sharedClient.storageArea.logYardSideArea ?? 160,
              name: storageAreaStore.generateDefaultName(),
              storageArea: {
                type: StorageAreaType.SortedPile,
                allowedTypes: [StorageAreaType.SortedPile, StorageAreaType.UnsortedPile, StorageAreaType.CoilStack, StorageAreaType.Bulk]
              }
            });
          },
          visible() {
            return authStore.isAdmin;
          }
        },
        {
          label: contextMenuTranslations.create.logSorter,
          command() {
            Assert.definied(contextMenuCoordinates);
            selectedLogSorterStore.create({
              coordinate: contextMenuCoordinates
            });
          },
          visible() {
            return authStore.isAdmin;
          }
        },
        {
          label: contextMenuTranslations.create.intake,
          command() {
            Assert.definied(contextMenuCoordinates);
            selectedShapeStore.create({
              coordinate: contextMenuCoordinates,
              width: 20,
              length: 20,
              storageArea: {
                type: StorageAreaType.LogSorterIntake,
                allowedTypes: [StorageAreaType.LogSorterIntake, StorageAreaType.SawLineIntake]
              }
            });
          },
          visible() {
            return authStore.isAdmin;
          }
        }
      ]
    },
    {
      label: translations.value.generic.edit,
      visible: () => {
        return contextMenuFeature === null && authStore.isAdmin;
      },
      items: [
        {
          label: translations.value.generic.logSorter,
          command() {
            onEditLogSorterClick();
          },
          visible() {
            return authStore.isAdmin;
          }
        },
      ],
    },
    {
      label: contextMenuTranslations.audit,
      async command() {
        if (!contextMenuFeature) return;

        const shapeId = getShapeId(contextMenuFeature);

        if (shapeId !== undefined) {
          const response = await shapeAuditApi.execute({
            id: shapeId
          });

          if (!response.ok) {
            actionFailedAlert.show(response.details);
            return;
          }

          auditDialog.value?.show({
            audit: response.audit
          });
          return;
        }

        const vehicleId = getGeoJsonPropertyText(contextMenuFeature, 'vehicleId');

        if (vehicleId) {
          const response = await vehicleAuditApi.execute({
            id: vehicleId
          });

          if (!response.ok) {
            actionFailedAlert.show(response.details);
            return;
          }

          auditDialog.value?.show({
            audit: response.audit
          });
          return;
        }
      },
      visible() {
        if (!authStore.isSystemAdmin) return false;
        if (contextMenuFeature === null) return false;
        const shapeId = getShapeId(contextMenuFeature);
        const vehicleId = getGeoJsonPropertyText(contextMenuFeature, 'vehicleId');
        return shapeId !== undefined || vehicleId !== undefined;
      }
    },
    {
      label: contextMenuTranslations.copy,
      async command() {
        Assert.definied(contextMenuFeature);
        const shapeId = getShapeId(contextMenuFeature);
        Assert.definied(shapeId);
        selectedShapeStore.copy({
          shapeId: shapeId,
        });
      },
      visible() {
        if (!authStore.isAdmin) return false;
        if (contextMenuFeature === null) return false;
        const shapeId = getShapeId(contextMenuFeature);
        return shapeId !== undefined;
      }
    },
    {
      label: contextMenuTranslations.move,
      command() {
        if (!contextMenuFeature) return;
        const contextMenuStorageAreaId = getStorageAreaId(contextMenuFeature);
        if (!contextMenuStorageAreaId) return;
        const contextMenuStorageArea = storageAreaStore.getById(contextMenuStorageAreaId);
        if (!contextMenuStorageArea) return;
        const selectedStorageAreaValue = selectedStorageArea.value;
        if (!selectedStorageAreaValue) return;

        if (isCoilStorage(contextMenuStorageArea.type) && isCoilStorage(selectedStorageAreaValue.type) && moveCoilDialog.value) {
          moveCoilDialog.value.show({
            fromStorageAreaId: selectedStorageAreaValue.id,
            toStorageAreaId: contextMenuStorageArea.id
          });
        }

        if (isLogYard(contextMenuStorageArea.type) && isLogYard(selectedStorageAreaValue.type) && moveLogYardContentDialog.value) {
          moveLogYardContentDialog.value.show({
            fromStorageAreaId: selectedStorageAreaValue.id,
            toStorageAreaId: contextMenuStorageArea.id
          });
        }

        if (isBulkStorage(contextMenuStorageArea.type) && isBulkStorage(selectedStorageAreaValue.type) && moveBulkContentDialog.value) {
          moveBulkContentDialog.value.show({
            fromStorageAreaId: selectedStorageAreaValue.id,
            toStorageAreaId: contextMenuStorageArea.id
          });
        }
      },
      visible() {

        const storageAreaType = selectedStorageArea.value?.type;

        if (!storageAreaType) return false;
        if (!contextMenuFeature) return false;
        const contextMenuStorageAreaId = getStorageAreaId(contextMenuFeature);
        if (!contextMenuStorageAreaId) return false;
        const contextMenuStorageArea = storageAreaStore.getById(contextMenuStorageAreaId);
        if (!contextMenuStorageArea) return false;

        return (isCoilStorage(storageAreaType) && isCoilStorage(contextMenuStorageArea.type)) ||
          (isLogYard(storageAreaType) && isLogYard(contextMenuStorageArea.type)) ||
          (isBulkStorage(storageAreaType) && isBulkStorage(contextMenuStorageArea.type));
      }
    }
  ]
});

function hasVisibleItems(items: MenuItem[] | undefined): boolean {
  if (!items?.length) return false

  return items.some(item => {
    // Compute actual visibility
    let visible = true
    if (typeof item.visible === 'function') {
      try {
        visible = item.visible() !== false
      } catch {
        visible = false
      }
    } else if (item.visible === false) {
      visible = false
    }

    if (item.items) {
      const hasVisibleChildren = hasVisibleItems(item.items)

      return visible && hasVisibleChildren;
    }

    return visible;
  });
}

const contextMenu = useTemplateRef('contextMenu');

onMounted(async () => {
  const rootElement = rootElementRef.value;
  if (!rootElement) throw new Error('Map div not found!');

  const { mapSettings } = storeToRefs(settingStore);
  await refreshSettingStore.initialize();

  const storedSelectedMapView = localStorage.getItem("selected-map-view");

  let selectedMapView: MapSettingOptions | null = null;
  if (storedSelectedMapView) {
    try {
      selectedMapView = JSON.parse(storedSelectedMapView) as MapSettingOptions;
    } catch (ex) {
      console.log("Unvalid json string in localStorage 'selected-map-view': ", storedSelectedMapView)
    }
  }

  const tileLayer = new TileLayer({
    source: new XYZ({
      url: 'api/map/osm/tile/{z}/{x}/{y}'
    })
  });

  const center = selectedMapView ?
    [selectedMapView.longitude, selectedMapView.latitude] :
    [mapSettings.value.longitude, mapSettings.value.latitude];

  const zoom = selectedMapView ? selectedMapView.zoom : mapSettings.value.zoom;

  const bearing = selectedMapView ? selectedMapView.bearing : mapSettings.value.bearing;

  const view = new View({
    center: center,
    zoom: zoom,
    rotation: bearing,
    minZoom: mapSettings.value.minZoom ?? 3,
  });

  const map = new OLMap({
    target: rootElement,
    view: view,
    layers: [tileLayer]
  });

  mapApi.value = {
    get bearing() {
      return view.getRotation();
    },
    get zoom() {
      return view.getZoom() ?? 15;
    },
    get center() {
      const center = view.getCenter();
      Assert.definied(center);
      return {
        longitude: center[0]!,
        latitude: center[1]!
      }
    },
    flyTo(options) {
      const { center, bearing, zoom } = options;

      view.animate({
        center: [center.longitude, center.latitude],
        rotation: bearing,
        zoom: zoom
      });
    },
  }

  watch(mapSettings, (mapSettings) => {
    view.animate({
      center: [mapSettings.longitude, mapSettings.latitude],
      rotation: mapSettings.bearing,
      zoom: mapSettings.zoom
    });
  });

  previewPolygonLayer.attachListeners(map);
  const shapeSource = shapeLayer.attach(map);
  const previewRectangleSource = previewRectangleLayer.attach(map);
  const previewPolygonSource = previewPolygonLayer.attach(map);
  previewLogsorterLayer.attach(map);
  const vehicleSource = vehiclesLayer.attach(map);

  const mapElement = map.getTargetElement();

  mapElement.addEventListener('contextmenu', (ev) => {
    if (contextMenuDisabled.value) return;

    const coordinate = map.getEventCoordinate(ev);

    contextMenuCoordinates = {
      longitude: coordinate[0]!,
      latitude: coordinate[1]!
    };

    const shapeFeatures = shapeSource.getFeaturesAtCoordinate(coordinate);
    const vehicleFeatures = vehicleSource.getFeaturesAtCoordinate(coordinate);
    const previewRectangleFeatures = previewRectangleSource.getFeaturesAtCoordinate(coordinate);
    const previewPolygonFeatures = previewPolygonSource.getFeaturesAtCoordinate(coordinate);

    const features = [...shapeFeatures, ...vehicleFeatures, ...previewRectangleFeatures, ...previewPolygonFeatures];

    if (features.length > 0) {
      contextMenuFeature = features[0]!;
    } else {
      contextMenuFeature = null;
    }

    if (!hasVisibleItems(menuItems.value)) {
      return;
    }

    contextMenu.value?.show(ev);
  });
});

setupVehicleLayer();
setupShapeLayer();

function setupVehicleLayer() {

  onMounted(async () => {
    await refreshVehicleStore.initialize();
  });

  newPosition.on((position) => {
    const timestamp = new Date(position.clientTimestampUTC);

    vehiclesLayer.mergePosition(position.vehicleId, {
      latitude: position.latitude,
      longitude: position.longitude,
      timestamp: timestamp.getTime(),
      heading: position.heading
    });
  });
}

function setupShapeLayer() {

  shapeLayer.addClickListener(async (ev) => {
    if (!selectedShapeStore.selectionEnabled) return;

    const unsavedChangesUi = unsavedChangesDialog.value;
    const shapeUi = shapeDrawer.value;

    if (unsavedChangesUi && shapeUi && shapeUi.changed) {
      const unsavedResult = await unsavedChangesDialog.value?.show({
        saveDisabled: shapeUi.saveDisabled
      });

      switch (unsavedResult) {
        case 'Cancel':
          return;
        case 'Discard':
          break;
        case 'Save':
          await shapeUi.save();
          break;
      }
    }

    const shapeId = ev.shapeId;
    const multiple = ev.multiple;

    if (shapeId !== null) {
      if (selectedShapeStore.selectedShapeIds.indexOf(shapeId) !== -1) {
        if (multiple) {
          selectedShapeStore.deselect(shapeId);
        }

        return;
      };
      await selectedShapeStore.edit({ shapeId, multiple });
    } else {
      selectedShapeStore.deselect(null);
    }
  });

  const { storageAreaIds } = storeToRefs(selectedLogSorterStore);

  watch(storageAreaIds, (newStorageAreaIds) => {
    shapeLayer.hideStorageArea((ids) => {
      ids.clear();
      for (const id of newStorageAreaIds) {
        ids.add(id);
      }
    })
  });

  const { selectedShapeIds } = storeToRefs(selectedShapeStore);

  watch(selectedShapeIds, (newSelectedShapeIds) => {
    shapeLayer.hideShapes((ids) => {
      ids.clear();
      for (const id of newSelectedShapeIds) {
        ids.add(id);
      }
    });
  });

  onMounted(async () => {
    await refreshProductStore.initialize();
    await refreshStorageAreaStore.initialize();
    await refreshShapeStore.initialize();
    await refreshSettingStore.initialize();
    await refreshCoilStore.initialize();
    await refreshProductGroupStore.initialize();
    await refreshProductionOrderStore.initialize();
  });
}

function onSearchClick() {
  Assert.definied(searchLogYardDialogRef.value);
  searchLogYardDialogRef.value.visible = true;
}

async function onMoveTimberFromLogSorterBins() {
  const response = await moveTimberFromLogSorterBinsApi.execute();
  if (!response.ok) {
    actionFailedAlert.show(response.details);
  }
}

function executeSearch() {
  Assert.definied(searchLogYardDialogRef.value);
  Assert.definied(searchLogYardDialogRef.value.selectedProduct);

  const foundIds = useSearchProduct(searchLogYardDialogRef.value.selectedProduct.id, storageAreaStore.storageAreas);
  shapeLayer.searchStorageAreas((ids) => {
    ids.clear();
    foundIds.forEach(id => {
      ids.add(id);
    });
  });
}

function clearSearch() {
  shapeLayer.clearSearchStorageAreas();
}

async function onEditLogSorterClick() {

  const response = await getLogSorters.execute();

  if (response.ok) {
    logSorters.value = response.logSorter

    if (logSorters.value.length == 0) return;
    if (logSorters.value.length == 1) {

      const editLogSorter: EditLogSorter = {
        logSorter: logSorters.value[0]!
      }
      selectedLogSorterStore.edit(editLogSorter);
    } else if (logSorters.value.length > 1) {
      selectLogSorterDialogVisible.value = true;
    }
  }
};

function selectEditLogSorter() {
  if (selectedLogSorter.value) {
    const editLogSorter: EditLogSorter = {
      logSorter: selectedLogSorter.value
    }
    selectedLogSorterStore.edit(editLogSorter);
    selectLogSorterDialogVisible.value = false;
  }
}

</script>

<template>
  <MainLayout>
    <template #default>
      <div class="map-view" ref="map-view"></div>
    </template>
    <template #header>
      <Button v-if="featureFlags.isFeatureEnabled('MoveTimberFromLogSorterBins')"
        :label="translations.moves.moveTimberFromLogSorterBins" :loading="moveTimberFromLogSorterBinsApi.loading.value"
        @click="onMoveTimberFromLogSorterBins"></Button>
      <Button :severity="searchLogYardDialogRef?.searchActive ? 'success' : 'primary'" @click="onSearchClick"
        :loading="saveMapModeLoading" icon="pi pi-search" :label="translations.generic.search">
      </Button>
      <MapView v-if="mapApi" :map-api="mapApi"></MapView>
      <MapStyleSelect></MapStyleSelect>
    </template>
  </MainLayout>
  <ShapeDrawer ref="shape-drawer"></ShapeDrawer>
  <LogSorterDrawer></LogSorterDrawer>
  <ContextMenu ref="contextMenu" :model="menuItems"></ContextMenu>
  <LogYardEditContentDialog></LogYardEditContentDialog>
  <BulkEditContentDialog></BulkEditContentDialog>
  <UnsavedChangesDialog ref="unsaved-changes"></UnsavedChangesDialog>
  <AuditDialog ref="audit-dialog"></AuditDialog>
  <MoveCoilDialog ref="move-coil-dialog"></MoveCoilDialog>
  <MoveLogYardContentDialog ref="move-log-yard-content-dialog"></MoveLogYardContentDialog>
  <MoveBulkContentDialog ref="move-bulk-content-dialog"></MoveBulkContentDialog>
  <SearchLogYardDialog ref="search-log-yard-dialog" @execute-product-search="executeSearch"
    @clear-product-search="clearSearch"></SearchLogYardDialog>
  <Dialog v-model:visible="selectLogSorterDialogVisible" modal :header="translations.dialogs.selectLogSorter.title"
    :style="{ width: '25rem' }">
    <label for="selectLogSorter">{{ translations.generic.logSorter }}</label>
    <Select id="selectLogSorter" v-model="selectedLogSorter" :options="logSorters" optionLabel="name" fluid></Select>
    <div class="flex justify-end gap-2 pt-3">
      <Button type="button" :label="translations.generic.edit" @click="selectEditLogSorter"></Button>
      <Button type="button" :label="translations.generic.abort" severity="secondary"
        @click="selectLogSorterDialogVisible = false"></Button>
    </div>
  </Dialog>
</template>

<style scoped>
.map-view {
  height: 100%;
}
</style>
